// apps/web/src/pages/pcp/conferencia-pv/item-do-pv-select.tsx — Fase 12, Planos 10 e 29 (NFE-05, D-08, D-12).
// Campo "Item do PV" por item da conferência da NF: sugestão FIFO pré-selecionada, 'Dividir entre itens…', 'Sem PV' e,
// quando a quantidade passa do saldo do item de PV, alerta amber + justificativa obrigatória (>= 5 caracteres).
// Sem candidatos só existe a opção 'Sem PV'. Vocabulário visual (Tailwind cru) da modal da conferência.
import { useState, type ReactElement } from 'react';
import type { CandidatoItemPv } from '@pieltec/contracts';
import {
  DIVIDIR_ENTRE_ITENS,
  MINIMO_JUSTIFICATIVA,
  SEM_PV,
  divisaoValida,
  excedenteDaAlocacao,
  mensagemExcedente,
  rotuloOpcaoItemPv,
  type AlocacaoEmEdicao,
} from '../../../lib/vendas/recebimento-pv';
import { DividirEntreItensDialog } from './dividir-entre-itens-dialog';
import { CampoSelect } from '../../../components/ui/campo-select';

const FORMATO_QUANTIDADE = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 });
const FORMATO_PRECO = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 6 });

function precoDoPv(candidato: CandidatoItemPv): string {
  return candidato.precoUnitario == null ? 'Preço pendente no PV' : `Preço unitário do PV: ${FORMATO_PRECO.format(candidato.precoUnitario)}`;
}

export interface ItemDoPvSelectProps {
  readonly documentoFiscalItemId: string;
  readonly candidatos: readonly CandidatoItemPv[];
  /** `pedidoVendaItemId` escolhido, `DIVIDIR_ENTRE_ITENS` ou '' (`SEM_PV`) para receber sem PV. */
  readonly valor: string;
  readonly quantidadeFisica: number;
  readonly disabled?: boolean;
  readonly onChange: (valor: string) => void;
  /** Justificativa do excedente quando um único item de PV é escolhido. */
  readonly justificativa: string;
  readonly onJustificativaChange: (justificativa: string) => void;
  /** Divisão editada (ou sugerida) quando `valor` é `DIVIDIR_ENTRE_ITENS`. */
  readonly divisao: readonly AlocacaoEmEdicao[];
  readonly onDivisaoChange: (divisao: AlocacaoEmEdicao[]) => void;
}

export function ItemDoPvSelect({
  documentoFiscalItemId,
  candidatos,
  valor,
  quantidadeFisica,
  disabled = false,
  onChange,
  justificativa,
  onJustificativaChange,
  divisao,
  onDivisaoChange,
}: ItemDoPvSelectProps): ReactElement {
  const [dividindo, setDividindo] = useState(false);
  const escolhido = candidatos.find((candidato) => candidato.pedidoVendaItemId === valor);
  const excedente = escolhido !== undefined ? excedenteDaAlocacao({ quantidade: quantidadeFisica, saldoAReceber: escolhido.saldoAReceber }) : 0;
  const idCampo = `item-do-pv-${documentoFiscalItemId}`;
  const idJustificativa = `${idCampo}-justificativa`;
  const semCandidatos = candidatos.length === 0;

  const aoEscolher = (proximo: string) => {
    // A divisão só vale depois de confirmada no diálogo; cancelar mantém a escolha anterior.
    if (proximo === DIVIDIR_ENTRE_ITENS) {
      setDividindo(true);
      return;
    }
    onChange(proximo);
  };

  return (
    <div className="text-xs space-y-1">
      <label htmlFor={idCampo} className="block text-slate-600">
        Item do PV
      </label>
      <CampoSelect
        id={idCampo}
        value={valor}
        disabled={semCandidatos || disabled}
        onValueChange={aoEscolher}
        options={[
          ...candidatos.map((candidato) => ({ value: candidato.pedidoVendaItemId, label: rotuloOpcaoItemPv(candidato) })),
          ...(semCandidatos ? [] : [{ value: DIVIDIR_ENTRE_ITENS, label: 'Dividir entre itens…' }]),
          { value: SEM_PV, label: 'Sem PV' },
        ]}
        className="min-h-11 text-sm"
      />

      {semCandidatos && <p className="text-slate-500">Nenhum item de PV aberto para este código. O recebimento segue sem PV.</p>}

      {escolhido !== undefined && (
        <>
          <p className="text-slate-500 truncate" title={escolhido.descricao}>
            {escolhido.descricao}
          </p>
          <p className={escolhido.precoUnitario == null ? 'text-amber-700' : 'text-slate-700 font-medium'}>{precoDoPv(escolhido)}</p>
          {escolhido.materialId === null && <p className="text-rose-700">Este item do PV não tem material vinculado. Vincule o material no PV antes de receber.</p>}
          {candidatos[0]?.pedidoVendaItemId === escolhido.pedidoVendaItemId && <p className="text-slate-500">Sugestão FIFO: item de PV com o prazo mais antigo.</p>}
        </>
      )}

      {escolhido !== undefined && excedente > 0 && (
        <div className="space-y-2">
          <div className="p-2.5 bg-amber-50 border border-amber-200 text-amber-900 rounded leading-relaxed" role="status">
            {mensagemExcedente(excedente)}
          </div>
          <div>
            <label htmlFor={idJustificativa} className="block text-slate-600 mb-1">
              Justificativa (obrigatória)
            </label>
            <textarea
              id={idJustificativa}
              rows={2}
              disabled={disabled}
              value={justificativa}
              onChange={(evento) => onJustificativaChange(evento.target.value)}
              aria-invalid={justificativa.trim().length < MINIMO_JUSTIFICATIVA}
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
            />
          </div>
        </div>
      )}

      {valor === DIVIDIR_ENTRE_ITENS && (
        <div className="p-2.5 bg-slate-100 border border-slate-200 rounded space-y-1">
          <ul className="text-slate-700 space-y-0.5">
            {divisao
              .filter((parte) => parte.quantidade > 0)
              .map((parte) => {
                const candidato = candidatos.find((c) => c.pedidoVendaItemId === parte.pedidoVendaItemId);
                return (
                  <li key={parte.pedidoVendaItemId} className="truncate">
                    {candidato ? `${candidato.pvNumero} · item ${candidato.numeroItem}` : parte.pedidoVendaItemId}: <strong>{FORMATO_QUANTIDADE.format(parte.quantidade)}</strong>
                    {candidato && <span className="block text-slate-500">{precoDoPv(candidato)}</span>}
                    {excedenteDaAlocacao(parte) > 0 && parte.justificativa.trim().length < MINIMO_JUSTIFICATIVA ? ' (justificativa pendente)' : ''}
                  </li>
                );
              })}
          </ul>
          {!divisaoValida(divisao.map((parte) => parte.quantidade), quantidadeFisica) && (
            <p className="text-amber-700">A divisão não fecha com a quantidade física conferida. Edite a divisão para continuar.</p>
          )}
          <button type="button" disabled={disabled} onClick={() => setDividindo(true)} className="text-blue-600 underline font-medium disabled:text-slate-500">
            Editar divisão
          </button>
        </div>
      )}

      {dividindo && (
        <DividirEntreItensDialog
          documentoFiscalItemId={documentoFiscalItemId}
          candidatos={candidatos}
          quantidadeFisica={quantidadeFisica}
          inicial={divisao}
          onCancelar={() => setDividindo(false)}
          onConfirmar={(alocacoes) => {
            onDivisaoChange(alocacoes);
            onChange(DIVIDIR_ENTRE_ITENS);
            setDividindo(false);
          }}
        />
      )}
    </div>
  );
}
